import React, { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Menu, Sun, Moon } from "lucide-react";
import { useTheme } from "../hooks/useTheme";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_streamlined-events/artifacts/7p0ticvb_NP%20logo%201.png";

export const Header = ({ onScrollTo }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, toggleTheme, isDark } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      data-testid="header"
      className="fixed top-0 w-full z-50 transition-all duration-500"
      style={{
        height: "80px",
        background: scrolled
          ? (isDark ? "rgba(10,10,10,0.95)" : "rgba(250,249,246,0.95)")
          : "transparent",
        backdropFilter: scrolled ? "blur(8px)" : "none",
        borderBottom: scrolled ? `1px solid var(--np-border)` : "none",
      }}
    >
      <div className="flex items-center justify-between h-full px-5 md:px-[7.6923%]">
        <img
          src={LOGO_URL}
          alt="Network Playground"
          className="h-24 object-contain cursor-pointer"
          style={{
            filter: isDark
              ? "drop-shadow(0 0 12px rgba(35,160,155,0.35)) drop-shadow(0 0 30px rgba(35,160,155,0.15))"
              : "none",
          }}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        />

        <nav className="hidden md:flex items-center gap-8">
          <button
            onClick={() => onScrollTo("experiences")}
            className="text-lg transition-colors duration-300"
            style={{ color: "var(--np-text-muted)" }}
            onMouseEnter={(e) => (e.target.style.color = "var(--np-text)")}
            onMouseLeave={(e) => (e.target.style.color = "var(--np-text-muted)")}
          >
            Experiences
          </button>
          <button
            onClick={() => onScrollTo("footer")}
            className="text-lg transition-colors duration-300"
            style={{ color: "var(--np-text-muted)" }}
            onMouseEnter={(e) => (e.target.style.color = "var(--np-text)")}
            onMouseLeave={(e) => (e.target.style.color = "var(--np-text-muted)")}
          >
            Contact
          </button>
          <button
            data-testid="theme-toggle"
            onClick={toggleTheme}
            className="p-2 transition-colors duration-300"
            style={{ color: "var(--np-text-muted)" }}
            onMouseEnter={(e) => (e.target.style.color = "var(--np-accent)")}
            onMouseLeave={(e) => (e.target.style.color = "var(--np-text-muted)")}
          >
            {isDark ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <button
            onClick={() => onScrollTo("experiences")}
            className="inline-flex items-center transition-all duration-500 font-medium text-lg px-8 py-4 gap-3 rounded-full"
            style={{
              background: "var(--np-cta)",
              color: "var(--np-cta-text)",
            }}
            onMouseEnter={(e) => (e.target.style.background = "var(--np-cta-hover)")}
            onMouseLeave={(e) => (e.target.style.background = "var(--np-cta)")}
          >
            Book Next Experience
          </button>
        </nav>

        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild className="md:hidden">
            <button className="p-2" style={{ color: "var(--np-text)" }}>
              <Menu size={24} />
            </button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="w-[280px]"
            style={{ background: "var(--np-bg)", borderColor: "var(--np-border)" }}
          >
            <div className="flex flex-col gap-8 pt-12">
              <button
                onClick={() => { onScrollTo("experiences"); setMobileOpen(false); }}
                className="text-xl text-left transition-colors"
                style={{ color: "var(--np-text)" }}
              >
                Experiences
              </button>
              <button
                onClick={() => { onScrollTo("footer"); setMobileOpen(false); }}
                className="text-xl text-left transition-colors"
                style={{ color: "var(--np-text)" }}
              >
                Contact
              </button>
              <button
                onClick={toggleTheme}
                className="flex items-center gap-2 text-lg transition-colors"
                style={{ color: "var(--np-text-secondary)" }}
              >
                {isDark ? <Sun size={18} /> : <Moon size={18} />}
                {isDark ? "Light Mode" : "Dark Mode"}
              </button>
              <button
                onClick={() => { onScrollTo("experiences"); setMobileOpen(false); }}
                className="px-6 py-3 text-lg font-medium w-full text-center rounded-full"
                style={{ background: "var(--np-cta)", color: "var(--np-cta-text)" }}
              >
                Book Next Experience
              </button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
